import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { ApiError } from '@/util/errors/api-error'
import { deleteMatterMember } from '@/matter/fetch/delete-matter-member'
import { postAddMatterMembers } from '@/matter/fetch/post-add-matter-members'
import { getOrganizationDirectoryUsers } from '@/organizations/fetch/get-organization-directory-users'
import type { OrganizationDirectoryUserRead } from '@/organizations/schemas'
import { CollaborationActions } from '@/collaboration/store/slice'
import { selectMatterMembers, selectMatterMembersError, selectMatterMembersInitialLoadComplete } from '@/collaboration/store/selectors'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getTrackEventGlobal, getAnalyticsApiErrorProps } from '@/analytics/utils/analytics-helpers'
import { getCollaborationOrganizationId } from '@/analytics/utils/matter-collaboration-analytics'
import { AuthContext } from '@/context/auth-context'

import { AddMembersStatus, MemberListStatus, type MatterCollaborator, type OrgUserOption, type RemoveMemberResult } from './types'
import { AddMemberOutcome, type AddMatterMemberResult, type MatterMemberRead } from './schemas'

const kUnavailableMemberName = 'Unavailable user'
const kUnavailableMemberEmail = 'Email unavailable'

export interface UseManageMatterMembersResult {
  collaborators: MatterCollaborator[]
  listStatus: MemberListStatus
  orgUsers: OrgUserOption[]
  addMembersStatus: AddMembersStatus
  /** Number of members added on the last fully successful add; drives the success toast text. */
  addedCount: number
  /** Name of the member removed by the last successful removal; drives the success toast text. */
  removedMemberName: string | null
  /** Add-flow failure message, shown above the footer actions. (Removal errors surface in the confirm modal.) */
  actionError: string | null
  addMembers: (userIds: string[]) => Promise<void>
  removeMember: (userId: string) => Promise<RemoveMemberResult>
}

function mapMemberReadToCollaborator(member: MatterMemberRead): MatterCollaborator {
  const email = member.email ?? kUnavailableMemberEmail

  return {
    userId: member.user_id,
    displayName: member.display_name ?? member.email ?? kUnavailableMemberName,
    email,
    orgRole: member.org_role,
    matterRole: member.matter_role,
    addedAt: member.accepted_at ?? '',
  }
}

function mapDirectoryUserToOrgUser(user: OrganizationDirectoryUserRead): OrgUserOption {
  const email = user.email ?? kUnavailableMemberEmail

  return {
    userId: user.user_id,
    displayName: user.display_name ?? email,
    email,
  }
}

function getAddMembersErrorMessage(results: AddMatterMemberResult[]): string | null {
  const invitedCount = results.filter((result) => result.outcome === AddMemberOutcome.INVITED).length
  const alreadyInvitedCount = results.filter((result) => result.outcome === AddMemberOutcome.ALREADY_INVITED).length
  const alreadyMemberCount = results.filter((result) => result.outcome === AddMemberOutcome.ALREADY_MEMBER).length
  const notFoundCount = results.filter((result) => result.outcome === AddMemberOutcome.NOT_FOUND).length

  if (invitedCount === results.length) {
    return null
  }

  if (alreadyMemberCount === results.length) {
    return results.length === 1 ? 'That member is already on this matter.' : 'Those members are already on this matter.'
  }

  if (alreadyInvitedCount === results.length) {
    return results.length === 1 ? 'That member has already been invited to this matter.' : 'Those members have already been invited to this matter.'
  }

  const details: string[] = []

  if (alreadyMemberCount > 0) {
    details.push(alreadyMemberCount === 1 ? '1 member is already on this matter.' : `${alreadyMemberCount} members are already on this matter.`)
  }

  if (alreadyInvitedCount > 0) {
    details.push(alreadyInvitedCount === 1 ? '1 member has already been invited.' : `${alreadyInvitedCount} members have already been invited.`)
  }

  if (notFoundCount > 0) {
    details.push(notFoundCount === 1 ? '1 member could not be found.' : `${notFoundCount} members could not be found.`)
  }

  if (invitedCount > 0) {
    return `Some selected members were not added. ${details.join(' ')}`
  }

  return details.join(' ') || 'Could not add members. Try again.'
}

/**
 * Controls the Manage Members modal: the member list plus the actions to add and remove members
 * and the org directory to pick from.
 */
export function useManageMatterMembers(matterId: string, enabled: boolean): UseManageMatterMembersResult {
  const dispatch = useAppDispatch()
  const { userAccountData } = useContext(AuthContext)
  const organizationId = getCollaborationOrganizationId(userAccountData)

  // Member list lives in Redux; all consumers stay in sync automatically.
  const rawMembers = useAppSelector((state) => selectMatterMembers(state, enabled ? matterId : undefined))
  const membersError = useAppSelector((state) => selectMatterMembersError(state, enabled ? matterId : undefined))
  const membersInitialLoadComplete = useAppSelector((state) => selectMatterMembersInitialLoadComplete(state, enabled ? matterId : undefined))

  const collaborators = useMemo(() => rawMembers.map(mapMemberReadToCollaborator), [rawMembers])

  // A background poll failure shouldn't hide an already-loaded list — only show the error state
  // when we have no cached members to fall back on.
  const listStatus = membersInitialLoadComplete ? MemberListStatus.SUCCESS : membersError ? MemberListStatus.ERROR : MemberListStatus.LOADING

  // Ephemeral UI state — scoped to this modal session, not shared.
  const [addMembersStatus, setAddMembersStatus] = useState<AddMembersStatus>(AddMembersStatus.IDLE)
  const [addedCount, setAddedCount] = useState(0)
  const [removedMemberName, setRemovedMemberName] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [orgUsers, setOrgUsers] = useState<OrgUserOption[]>([])

  const refreshOrgUsers = useCallback(async () => {
    if (!enabled || !matterId) return

    try {
      const response = await getOrganizationDirectoryUsers()
      setOrgUsers(response.users.map(mapDirectoryUserToOrgUser))
    } catch {
      setOrgUsers([])
    }
  }, [enabled, matterId])

  useEffect(() => {
    if (!enabled || !matterId) {
      setOrgUsers([])
      setAddMembersStatus(AddMembersStatus.IDLE)
      setAddedCount(0)
      setRemovedMemberName(null)
      setActionError(null)
      return
    }

    dispatch(CollaborationActions.fetchMembers({ matterId }))
    void refreshOrgUsers()
  }, [enabled, matterId, dispatch, refreshOrgUsers])

  const addMembers = useCallback(
    async (userIds: string[]) => {
      if (!enabled || !matterId || userIds.length === 0) return

      setActionError(null)
      setAddMembersStatus(AddMembersStatus.LOADING)

      try {
        const response = await postAddMatterMembers(matterId, userIds)
        const errorMessage = getAddMembersErrorMessage(response.results)
        const invitedResults = response.results.filter((result) => result.outcome === AddMemberOutcome.INVITED)
        const track = getTrackEventGlobal()

        invitedResults.forEach((result) => {
          track?.(AnalyticsEvent.MatterInviteSent, {
            matterId,
            organizationId,
            recipientUserId: result.user_id,
            selectedRecipientCount: userIds.length,
            invitedCount: invitedResults.length,
          })
        })

        if (errorMessage) {
          setActionError(errorMessage)
          setAddMembersStatus(AddMembersStatus.ERROR)
          dispatch(CollaborationActions.fetchMembers({ matterId, force: true }))
        } else {
          // Refetch from the server rather than writing the add response, so an in-flight poll can't overwrite it.
          dispatch(CollaborationActions.fetchMembers({ matterId, force: true }))
          setAddedCount(response.results.length)
          setAddMembersStatus(AddMembersStatus.SUCCESS)
        }
      } catch (error) {
        getTrackEventGlobal()?.(AnalyticsEvent.MatterInviteSendFailed, {
          matterId,
          organizationId,
          selectedRecipientCount: userIds.length,
          ...getAnalyticsApiErrorProps(error),
        })
        setAddMembersStatus(AddMembersStatus.ERROR)
        setActionError('Could not add members. Try again.')
        dispatch(CollaborationActions.fetchMembers({ matterId, force: true }))
      }
    },
    [enabled, matterId, organizationId, dispatch]
  )

  const removeMember = useCallback(
    async (userId: string): Promise<RemoveMemberResult> => {
      if (!enabled || !matterId) return 'Could not remove member. Try again.'

      // Capture the name before the list refresh drops the member.
      const removedName = collaborators.find((c) => c.userId === userId)?.displayName ?? null

      // Reset first so the container's toast effect re-fires for every successful removal.
      setRemovedMemberName(null)

      let removeError: RemoveMemberResult = null
      try {
        await deleteMatterMember(matterId, userId)
        setRemovedMemberName(removedName)
      } catch (error) {
        removeError = error instanceof ApiError && error.status === 403 ? 'Only the matter owner can remove members.' : 'Could not remove member. Try again.'
      } finally {
        dispatch(CollaborationActions.fetchMembers({ matterId, force: true }))
      }
      return removeError
    },
    [enabled, matterId, dispatch, collaborators]
  )

  return { collaborators, listStatus, orgUsers, addMembersStatus, addedCount, removedMemberName, actionError, addMembers, removeMember }
}
